import AlignLeftOutlined from '@ant-design/icons/lib/icons/AlignLeftOutlined';
import CopyOutlined from '@ant-design/icons/lib/icons/CopyOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Row from 'antd/lib/row';
import Switch from 'antd/lib/switch';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { useState } from 'react';
import { CustomerVenLogsModal } from '../../../components/modals/CustomerVenLogsModal/CustomerVenLogsModal';
import { CompanySelector } from '../../../components/selectors/CompanySelector/CompanySelector';
import { SiteSelector } from '../../../components/selectors/SiteSelector/SiteSelector';
import { useAuth } from '../../../domain/auth/useAuth';
import { COMPANY_TYPE, ICompany } from '../../../domain/company/interface';
import { ISite } from '../../../domain/site/interface';
import { useSiteQuery } from '../../../domain/site/queries';
import { copyVenToClipboard } from '../../../domain/ven/copyVenToClipboard';
import { ICustomerVen } from '../../../domain/ven/interface';
import { makeCustomerVenForSite } from '../../../domain/ven/makeCustomerVen';
import { useSaveCustomerVenMutation } from '../../../domain/ven/queries';

type VenScope = 'company' | 'site' | 'control_provider';

function getVenInitialScope(ven: ICustomerVen | Partial<ICustomerVen>): VenScope {
    if (ven.site_id && ven.site_id !== -1) return 'site';

    if (ven.control_provider_id) return 'control_provider';

    return 'company';
}

interface IVenFormProps {
    ven: ICustomerVen | Partial<ICustomerVen>;
    onClose: Function;
    isEditMode: boolean;
}

export function CustomerVenFormModal({ ven, onClose, isEditMode }: IVenFormProps) {
    const { notification } = App.useApp();
    const [form] = Form.useForm();

    const auth = useAuth();
    const isAdminRole = auth?.user?.isAdminRoleType();
    const isControlProvider = !!auth?.user?.isControlProvider();
    const isCustomer = !!auth?.user?.isCustomer();
    const [showLogs, setShowLogs] = useState(false);

    const initialScope = getVenInitialScope(ven);
    const [venScope, setVenScope] = useState<VenScope>(initialScope === 'site' ? 'site' : isControlProvider ? 'control_provider' : initialScope);

    const companyId = Form.useWatch('company_id', form);
    const selectedSiteId = Form.useWatch('site_id', form);

    const isSiteLoadEnabled = venScope === 'site' && !!selectedSiteId;
    const { data: loadedSite, isLoading: isSiteLoading } = useSiteQuery(selectedSiteId, {
        include: 'company',
        enabled: isSiteLoadEnabled,
    });

    const { mutateAsync: saveCustomerVen, isLoading } = useSaveCustomerVenMutation();

    async function onFormFinish(updatedVen: ICustomerVen) {
        try {
            const effectiveControlProviderId = isControlProvider ? auth?.user?.company_id! : updatedVen.control_provider_id;

            const preparedVen = {
                ...updatedVen,
                ...(ven?.id && { id: ven.id }),
                site_id: venScope === 'site' ? updatedVen.site_id : null,
                control_provider_id: venScope === 'control_provider' ? effectiveControlProviderId : null,
                ven_cert: null,
                ven_key: null,
                ven_profile: '2.0b',
                mac_address: (updatedVen.mac_address || '').replaceAll(/[-:.]/gm, ''),
            } as ICustomerVen;

            await saveCustomerVen(preparedVen);

            notification.info({ key: 'ven-save-info', message: 'VEN saved' });
            onClose(ven);
        } catch (err: any) {
            notification.error({ key: 'ven-save-error', message: err.message || 'Cannot save VEN!' });
        }
    }

    function closeModal() {
        onClose();
    }

    const onCopy = async () => {
        await copyVenToClipboard(form.getFieldsValue());
    };

    const onPrefillForm = () => {
        if (!loadedSite) return;

        const generatedVen = makeCustomerVenForSite(loadedSite as ISite & { company: ICompany });

        form.setFieldsValue({
            ven_username: generatedVen.ven_username,
            ven_id: generatedVen.ven_id,
            ven_password: generatedVen.ven_password,
            ven_uri: generatedVen.ven_uri,
            ven_vtn_id: generatedVen.ven_vtn_id,
            ven_market_context: generatedVen.ven_market_context,
        });
    };

    const handleCompanyChange = (value: number | null) => {
        form.setFieldValue('company_id', value);

        if (companyId !== value) {
            form.setFieldValue('site_id', null);
            form.setFieldValue('control_provider_id', null);
        }
    };

    const handleScopeChange = (scope: VenScope) => {
        setVenScope(scope);
        form.setFieldValue('site_id', null);
        form.setFieldValue('control_provider_id', null);
    };

    const showLogsModal = () => {
        setShowLogs(true);
    };

    const closeLogsModal = () => {
        setShowLogs(false);
    };

    return (
        <Modal
            title={isEditMode ? 'Edit Customer VEN' : 'New Customer VEN'}
            open
            destroyOnHidden
            onCancel={closeModal}
            footer={[
                isAdminRole && isEditMode && (
                    <div key="ven-modal-show-logs" style={{ width: '100%', display: 'flex' }}>
                        <Button icon={<AlignLeftOutlined />} onClick={showLogsModal}>
                            Show Logs
                        </Button>
                    </div>
                ),
                <Button key="ven-modal-cancel" onClick={closeModal}>
                    Cancel
                </Button>,
                <Button key="ven-modal-submit" type="primary" loading={isLoading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
            data-cy="customer-ven-modal"
        >
            <Form
                form={form}
                name="ven-form"
                preserve={false}
                layout="vertical"
                onFinish={onFormFinish}
                initialValues={{
                    ...ven,
                    ...(!isEditMode && { ven_enabled: true }),
                    ...(isControlProvider && !ven.control_provider_id && { control_provider_id: auth?.user?.company_id }),
                    ...(isCustomer && !ven.company_id && { company_id: auth?.user?.company_id }),
                }}
            >
                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            hasFeedback
                            name="company_id"
                            label={<Typography.Text strong>Company</Typography.Text>}
                            rules={[{ required: true, message: 'Please select company!' }]}
                        >
                            <CompanySelector onChange={handleCompanyChange} onlyTypes={[COMPANY_TYPE.CUSTOMER_COMPANY]} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label={<Typography.Text strong>VEN Scope</Typography.Text>}
                            tooltip={
                                <div>
                                    <div>Select the scope of events to return to the customer:</div>
                                    <div>
                                        <b>All Company Sites</b> — All events across the entire company
                                    </div>
                                    <div>
                                        <b>Control Provider Sites</b> — Events from all sites controlled by the selected control provider
                                    </div>
                                    <div>
                                        <b>Single Site</b> — Events from only the selected site
                                    </div>
                                </div>
                            }
                        >
                            <Radio.Group
                                value={venScope}
                                onChange={e => handleScopeChange(e.target.value)}
                                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                            >
                                <Radio value="company" disabled={isControlProvider}>
                                    All Company Sites
                                </Radio>
                                <Radio value="control_provider">Control Provider Sites</Radio>
                                <Radio value="site">Single Site</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>

                    {venScope === 'site' && (
                        <>
                            <Col span={12}>
                                <Form.Item
                                    name="site_id"
                                    label={<Typography.Text strong>Site</Typography.Text>}
                                    rules={[{ required: true, message: 'Please select site!' }]}
                                >
                                    <SiteSelector companyId={companyId} />
                                    {!companyId && <Typography.Text type="secondary">Select company first to see sites</Typography.Text>}
                                </Form.Item>
                            </Col>
                            {loadedSite && (
                                <Col span={12} style={{ marginBottom: 12, marginTop: -8 }}>
                                    <Tooltip title="Fill the form with generated username, password and venId...">
                                        <Button type="link" onClick={onPrefillForm} loading={isSiteLoading}>
                                            Prefill the form
                                        </Button>
                                    </Tooltip>
                                </Col>
                            )}
                        </>
                    )}

                    {!isControlProvider && venScope === 'control_provider' && (
                        <Col span={12}>
                            <Form.Item
                                name="control_provider_id"
                                label={<Typography.Text strong>Control Provider</Typography.Text>}
                                rules={[{ required: true, message: 'Please select control provider!' }]}
                            >
                                <CompanySelector
                                    placeholder="Select Control Provider"
                                    onlyTypes={[COMPANY_TYPE.CONTROL_PROVIDER]}
                                    disabled={isControlProvider}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item
                            name="ven_username"
                            label={<Typography.Text strong>Username</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter username!' },
                                { max: 50, message: 'Number of characters should be less than 50' },
                            ]}
                        >
                            <Input placeholder="Username" size="large" autoComplete="customer-ven-username" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ven_password"
                            label={<Typography.Text strong>Password</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter password!' },
                                { max: 50, message: 'Number of characters should be less than 50' },
                            ]}
                        >
                            <Input.Password placeholder="Password" size="large" autoComplete="new-password" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item
                            name="ven_id"
                            label={<Typography.Text strong>VEN ID</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter VEN ID!' },
                                { max: 50, message: 'Number of characters should be less than 50' },
                            ]}
                        >
                            <Input placeholder="VEN ID" size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ven_vtn_id"
                            label={<Typography.Text strong>VTN ID</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter VTN ID!' },
                                { max: 30, message: 'Number of characters should be less than 30' },
                            ]}
                        >
                            <Input placeholder="VTN ID" size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="ven_uri"
                    label={<Typography.Text strong>URI</Typography.Text>}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please enter VEN URI!' },
                        { max: 200, message: 'Number of characters should be less than 200' },
                    ]}
                >
                    <Input placeholder="URI" size="large" />
                </Form.Item>

                <Form.Item
                    name="mac_address"
                    tooltip="If it is an Enersponse hardware device, set its MAC address"
                    label={<Typography.Text strong>MAC Address</Typography.Text>}
                    hasFeedback
                    rules={[
                        {
                            pattern: /^(?:[0-9A-Fa-f]{2}([:-]?)[0-9A-Fa-f]{2})(?:(?:\1|\.)(?:[0-9A-Fa-f]{2}([:-]?)[0-9A-Fa-f]{2})){2}$/gm,
                            message:
                                'MAC address should be represented in one of the formats: six pairs of the characters separated with a hyphen (-) or colon(:); or three groups of four hexadecimal digits separated by dots(.); or 12 hexadecimal digits.',
                        },
                    ]}
                >
                    <Input placeholder="FFFFFFFFFFFF" size="large" />
                </Form.Item>

                <Row justify="end">
                    <Button key="copy-ven-info" type="primary" onClick={onCopy} icon={<CopyOutlined />}>
                        Copy VEN Properties
                    </Button>
                </Row>

                <Divider plain>OpenADR Options</Divider>

                <Form.Item
                    name="ven_market_context"
                    rules={[{ max: 1000, message: 'Number of characters should be less than 1000' }]}
                    label={<Typography.Text strong>Market Context</Typography.Text>}
                >
                    <Input placeholder="Market Context" size="large" />
                </Form.Item>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item
                            name="support_price_and_clean_response_market_context"
                            tooltip="If enabled, VEN will receive price and clean response market context for that types of events"
                            label={<Typography.Text strong>Support PR/CR Market Context</Typography.Text>}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="ven_registration_id" label={<Typography.Text strong>Registration ID</Typography.Text>}>
                            <Input size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={8}>
                    {auth?.user?.isAdmin() && (
                        <Col span={12}>
                            <Form.Item
                                name="ven_detailed_logging"
                                tooltip="If enabled, more details about communication will be logged"
                                label={<Typography.Text strong>Detailed Logging</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    )}

                    <Col span={12}>
                        <Form.Item
                            name="ven_enabled"
                            tooltip="You can temporary disable VEN, instead of deleting it"
                            label={<Typography.Text strong>Enable VEN</Typography.Text>}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {showLogs && <CustomerVenLogsModal onClose={closeLogsModal} ven={ven} />}
        </Modal>
    );
}
